import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress,
  Container, Grid, Stack, TextField, Typography
} from '@mui/material';
import {
  ArrowForward, Email, Instagram, LocalBar, LocationOn, OpenInNew, Star,
  Storefront,
  WhatsApp, WineBar
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import contactService from '../services/contactService';
import googleReviewsService from '../services/googleReviewsService';
import { useBusiness } from '../context/BusinessContext';
import { WHATSAPP_BASE_URL } from '../services/api';
import PublicHeader from '../components/PublicHeader';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import PublicFooter from '../components/PublicFooter';
import './Home.css';

const BRAND = {
  name: 'Descorcha2',
  description: 'Vinos que cuentan historias. Elegí el próximo para la tuya.'
};

const experiences = [
  { icon: <WineBar />, title: 'Vinos seleccionados', text: 'Etiquetas locales y nacionales elegidas para cada gusto, ocasión y presupuesto.' },
  { icon: <LocalBar />, title: 'Espumantes y bebidas', text: 'Una cava completa con opciones para regalar, celebrar o disfrutar sin apuro.' },
  { icon: <Storefront />, title: 'Atención personalizada', text: 'Te ayudamos a encontrar la botella indicada y a descubrir algo nuevo.' }
];

const Home = () => {
  const navigate = useNavigate();
  const { businessInfo } = useBusiness();
  const [sending, setSending] = useState(false);
  const [messageState, setMessageState] = useState({ type: '', text: '' });
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [googleReviews, setGoogleReviews] = useState(null);

  useEffect(() => {
    googleReviewsService.getReviews().then((result) => {
      if (result.success) setGoogleReviews(result.data);
    });
  }, []);

  useEffect(() => {
    if (!window.location.hash) return;
    const sectionId = window.location.hash.slice(1);
    window.requestAnimationFrame(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  const address = businessInfo?.address || '';
  const phone = businessInfo?.phone || '';
  const whatsapp = businessInfo?.whatsapp || phone;
  const instagram = businessInfo?.instagram || '';
  const description = businessInfo?.description || BRAND.description;
  const whatsappPhone = whatsapp.replace(/[^0-9]/g, '');
  const mapQuery = [address, businessInfo?.city, businessInfo?.province].filter(Boolean).join(', ');

  const handleChange = ({ target: { name, value } }) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSending(true);
    setMessageState({ type: '', text: '' });
    if (form.message.trim().length < 10) {
      setMessageState({ type: 'warning', text: 'El mensaje debe tener al menos 10 caracteres.' });
      setSending(false);
      return;
    }
    const result = await contactService.send(form);
    if (result.success) {
      setMessageState({ type: 'success', text: result.message || '¡Gracias! Recibimos tu consulta y te responderemos pronto.' });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } else {
      setMessageState({ type: 'error', text: result.message || 'No pudimos enviar la consulta. También podés escribirnos por WhatsApp.' });
    }
    setSending(false);
  };

  return (
    <Box className="wine-home">
      <PublicHeader active="inicio" />
      <FloatingWhatsApp />

      <Box component="section" id="inicio" className="wine-hero">
        <Box component="video" className="wine-hero-video" autoPlay muted loop playsInline poster="/descorcha2-logo.png">
          <source src="/descorcha2-local.mp4" type="video/mp4" />
        </Box>
        <Box className="wine-hero-overlay" />
        <Container maxWidth="lg" className="wine-hero-content">
          <Box className="wine-eyebrow"><span /> VINOTECA EN SAN JUAN</Box>
          <Typography component="h1">El vino indicado<br /><em>para cada momento.</em></Typography>
          <Typography className="wine-hero-description">{description}</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button variant="contained" size="large" endIcon={<ArrowForward />} onClick={() => navigate('/cava')}>Ver vinos y bebidas</Button>
            {whatsappPhone && <Button variant="outlined" size="large" startIcon={<WhatsApp />} href={`${WHATSAPP_BASE_URL}${whatsappPhone}`} target="_blank" rel="noreferrer">Escribinos</Button>}
          </Stack>
        </Container>
        {address && <Box className="wine-hero-location"><LocationOn /><span>{address}</span></Box>}
      </Box>

      <Box component="section" id="cava" className="wine-cava-section">
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box className="wine-section-label">NUESTRA CAVA</Box>
              <Typography variant="h2">Mucho más que<br /><em>elegir una botella.</em></Typography>
              <Typography className="wine-section-copy">
                En Descorcha2 reunimos una cuidada selección de vinos y bebidas. Queremos que comprar sea parte de la experiencia: mirar, descubrir, preguntar y llevarte exactamente lo que estabas buscando.
              </Typography>
              <Button className="wine-text-link" endIcon={<ArrowForward />} onClick={() => navigate('/cava')}>Conocé nuestra selección</Button>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {experiences.map((item, index) => (
                  <Grid item xs={12} sm={index === 2 ? 12 : 6} key={item.title}>
                    <Card className="wine-feature-card"><CardContent>
                      <Box className="wine-feature-icon">{item.icon}</Box>
                      <Typography variant="h5">{item.title}</Typography>
                      <Typography>{item.text}</Typography>
                    </CardContent></Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box className="wine-quote-section">
        <Container maxWidth="md">
          <WineBar />
          <Typography>“Cada botella guarda un lugar, una cosecha y una historia esperando ser descorchada.”</Typography>
          <span>DESCORCHA2 · WINES & DRINKS</span>
        </Container>
      </Box>

      {googleReviews && (
        <Box component="section" id="resenas" className="wine-reviews-section">
          <Container maxWidth="lg">
            <Box className="wine-reviews-heading">
              <Box>
                <Box className="wine-section-label">OPINIONES EN GOOGLE</Box>
                <Typography variant="h2">
                  {googleReviews.reviewCount > 0
                    ? 'La experiencia de quienes nos eligen.'
                    : 'Tu experiencia también forma parte de Descorcha2.'}
                </Typography>
              </Box>
              {googleReviews.reviewCount > 0 && (
                <Box className="wine-google-rating">
                  <Typography component="strong">{Number(googleReviews.rating).toFixed(1)}</Typography>
                  <Box className="wine-stars" aria-label={`${googleReviews.rating} de 5 estrellas`}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className={star <= Math.round(googleReviews.rating) ? 'is-filled' : ''} />
                    ))}
                  </Box>
                  <Typography>{googleReviews.reviewCount} reseñas en Google</Typography>
                </Box>
              )}
            </Box>

            {googleReviews.reviews?.length > 0 && (
              <Grid container spacing={3}>
                {googleReviews.reviews.map((review, index) => (
                  <Grid item xs={12} md={4} key={`${review.author}-${index}`}>
                    <Card className="wine-review-card">
                      <CardContent>
                        <Box className="wine-review-author">
                          {review.photoUrl ? (
                            <img src={review.photoUrl} alt="" referrerPolicy="no-referrer" />
                          ) : (
                            <span>{review.author.charAt(0).toUpperCase()}</span>
                          )}
                          <Box>
                            <Typography component="strong">{review.author}</Typography>
                            <Typography>{review.relativeTime}</Typography>
                          </Box>
                        </Box>
                        <Box className="wine-stars">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star key={star} className={star <= review.rating ? 'is-filled' : ''} />
                          ))}
                        </Box>
                        <Typography className="wine-review-text">“{review.text}”</Typography>
                        {review.googleMapsUrl && (
                          <Button href={review.googleMapsUrl} target="_blank" rel="noreferrer" endIcon={<OpenInNew />}>
                            Ver en Google
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} className="wine-review-actions">
              <Button variant="contained" href={googleReviews.writeReviewUrl} target="_blank" rel="noreferrer">
                Dejar una reseña
              </Button>
              <Button variant="outlined" href={googleReviews.googleMapsUrl} target="_blank" rel="noreferrer">
                Ver todas en Google
              </Button>
            </Stack>
          </Container>
        </Box>
      )}

      <Box component="section" id="contacto" className="wine-contact-section">
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }}>
            <Grid item xs={12} md={5}>
              <Box className="wine-section-label">HABLEMOS</Box>
              <Typography variant="h2">Estamos para ayudarte a elegir.</Typography>
              <Typography className="wine-section-copy">Consultanos por etiquetas, disponibilidad, regalos o recomendaciones.</Typography>
              <Stack spacing={2.5} className="wine-contact-links">
                {address && <Button startIcon={<LocationOn />} href={`https://maps.google.com/?q=${encodeURIComponent(mapQuery)}`} target="_blank">{address}</Button>}
                {whatsappPhone && <Button startIcon={<WhatsApp />} href={`${WHATSAPP_BASE_URL}${whatsappPhone}`} target="_blank">WhatsApp · {whatsapp}</Button>}
                {instagram && <Button startIcon={<Instagram />} href={instagram} target="_blank">{instagram.replace(/^https?:\/\/(www\.)?instagram\.com\//, '@').replace(/\/$/, '')}</Button>}
                {businessInfo?.email && <Button startIcon={<Email />} href={`mailto:${businessInfo.email}`}>{businessInfo.email}</Button>}
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Card className="wine-contact-card"><CardContent>
                <Typography variant="h4">Dejanos tu consulta</Typography>
                <Typography>Completá tus datos y te contactamos.</Typography>
                {messageState.text && <Alert severity={messageState.type} sx={{ mt: 3 }}>{messageState.text}</Alert>}
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}><TextField fullWidth required label="Nombre" name="name" value={form.name} onChange={handleChange} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth required type="email" label="Email" name="email" value={form.email} onChange={handleChange} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Teléfono" name="phone" value={form.phone} onChange={handleChange} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth label="Asunto" name="subject" value={form.subject} onChange={handleChange} /></Grid>
                    <Grid item xs={12}><TextField fullWidth required multiline rows={4} label="Mensaje" name="message" value={form.message} onChange={handleChange} inputProps={{ minLength: 10, maxLength: 5000 }} /></Grid>
                    <Grid item xs={12}>
                      <Button fullWidth type="submit" variant="contained" size="large" disabled={sending} endIcon={sending ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}>
                        {sending ? 'Enviando...' : 'Enviar consulta'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent></Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <PublicFooter />
    </Box>
  );
};

export default Home;
